import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Route as RouterRoute, Switch } from 'react-router-dom';

import { App } from './modules/App/App';
import IDEView from './modules/IDE/pages/IDEView';
import FullView from './modules/IDE/pages/FullView';
import { About } from './modules/About/pages/About';
import { CodeOfConduct } from './modules/Legal/pages/CodeOfConduct';
import { PrivacyPolicy } from './modules/Legal/pages/PrivacyPolicy';
import { TermsOfUse } from './modules/Legal/pages/TermsOfUse';
import { LoginView } from './modules/User/pages/LoginView';
import { SignupView } from './modules/User/pages/SignupView';
import { ResetPasswordView } from './modules/User/pages/ResetPasswordView';
import { AccountView } from './modules/User/pages/AccountView';
import DashboardView from './modules/User/pages/DashboardView';
import ExamplesView from './modules/User/pages/ExamplesView';
import DocsView from './modules/Docs/pages/DocsView';
import NotFound from './modules/App/pages/NotFound';
import { initAuthListener } from './modules/User/actions';
import ProtectedSketchRoute from './protected-route';
import RequireAuthRoute from './require-auth-route';

/**
 *  `params` is no longer a top-level route component prop in v4.
 *  It is a nested property of `match`.
 *  Use an HOC to lift it up to top-level.
 */
const withParams = (Component) => (props) => (
  // eslint-disable-next-line react/prop-types
  <Component {...props} params={props.match.params} />
);
/**
 * Instead of updating all individual components, use this plug-in Route replacement.
 * It passes the `params` as a top-level property
 * and fixes prop-types errors in react-router package
 * (Warning: Failed prop type: Invalid prop `component` of type `object` supplied to `Route`, expected `function`.)
 */
const Route = ({ component, ...props }) => (
  <RouterRoute component={withParams(component)} {...props} />
);
Route.propTypes = {
  ...RouterRoute.propTypes,
  component: PropTypes.elementType.isRequired
};

const routes = (
  <Switch>
    <ProtectedSketchRoute exact path="/" component={IDEView} />
    <Route path="/login" component={LoginView} />
    <Route path="/signup" component={SignupView} />
    <Route path="/reset-password" component={ResetPasswordView} />
    <Route path="/projects/:project_id" component={IDEView} />
    <ProtectedSketchRoute
      path="/:username/full/:project_id"
      component={FullView}
    />
    <ProtectedSketchRoute path="/full/:project_id" component={FullView} />

    <Route path="/:username/assets" component={DashboardView} />
    <ProtectedSketchRoute
      path="/:username/sketches/:project_id"
      component={IDEView}
    />
    <Route path="/:username/sketches" component={DashboardView} />
    <RequireAuthRoute
      path="/:username/shared"
      component={withParams(DashboardView)}
    />
    {/* No-username dashboard + account are the signed-in user's own pages: gate on auth. */}
    <RequireAuthRoute path="/sketches" component={withParams(DashboardView)} />
    <RequireAuthRoute path="/assets" component={withParams(DashboardView)} />
    <RequireAuthRoute path="/shared" component={withParams(DashboardView)} />
    <RequireAuthRoute path="/account" component={withParams(AccountView)} />
    {/* Curated AR examples — public (browsable logged-out); copying/editing still needs auth). */}
    <Route path="/examples" component={ExamplesView} />
    {/* AstroTour[AR] reference (Help menu) — public, like /examples and /about. */}
    <Route path="/ar-reference/:slug?" component={DocsView} />
    <Route path="/about" component={About} />
    <Route path="/privacy-policy" component={PrivacyPolicy} />
    <Route path="/terms-of-use" component={TermsOfUse} />
    <Route path="/code-of-conduct" component={CodeOfConduct} />
    {/* Catch-all: unknown URLs render a real 404 instead of a blank App shell. */}
    <Route component={NotFound} />
  </Switch>
);

function Routing() {
  const dispatch = useDispatch();

  useEffect(() => {
    const unsubscribe = dispatch(initAuthListener());
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [dispatch]);

  return <App>{routes}</App>;
}

export default Routing;
